import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';

interface Props {
  onAccepted: () => void;
}

export function TermsAcceptanceModal({ onAccepted }: Props) {
  const { updateUser, logout } = useAuth();
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    setAccepting(true);
    setError(null);
    try {
      await apiClient.acceptTerms();
      updateUser({ termsAccepted: true });
      onAccepted();
    } catch {
      setError('Failed to accept terms. Please try again.');
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    try {
      await logout();
    } catch {
      // ignore
    } finally {
      window.location.href = '/login';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-2xl w-full">
        {/* Header */}
        <div className="px-8 pt-8 pb-4 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Welcome — one last step</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Please read and accept our Terms &amp; Conditions before using the platform.
          </p>
        </div>

        {/* Scrollable summary */}
        <div className="px-8 py-5 max-h-72 overflow-y-auto text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <p><span className="font-semibold">Permitted use:</span> Academic, research, and educational purposes only. Commercial use requires written permission.</p>
          <p><span className="font-semibold">Prohibited:</span> Illegal or harmful content, bypassing AI safety measures, automated mass querying, impersonation, or academic misconduct (submitting AI output as your own without disclosure).</p>
          <p><span className="font-semibold">AI disclaimer:</span> Outputs may be inaccurate or incomplete. You are responsible for verifying all results before use.</p>
          <p><span className="font-semibold">Data &amp; privacy:</span> Your conversations are stored to provide the service. See our <Link to="/privacy" target="_blank" className="text-blue-600 dark:text-blue-400 hover:underline">Privacy Policy</Link> for full details and your GDPR rights.</p>
          <p><span className="font-semibold">Rate limits:</span> Message and token limits apply per user and may be adjusted without notice.</p>
          <p><span className="font-semibold">Governing law:</span> German law applies. Full terms at <Link to="/terms" target="_blank" className="text-blue-600 dark:text-blue-400 hover:underline">Terms &amp; Conditions</Link>.</p>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-gray-100 dark:border-gray-800">
          {error && <p className="text-red-600 dark:text-red-400 text-sm mb-3">{error}</p>}
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button
              onClick={handleDecline}
              className="px-5 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm font-medium transition-colors"
            >
              Decline &amp; Sign Out
            </button>
            <button
              onClick={handleAccept}
              disabled={accepting}
              className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold transition-colors"
            >
              {accepting ? 'Accepting...' : 'I Accept'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
